import '@/popup/styles/main.scss'
import { createPinia } from 'pinia'
import browser from 'webextension-polyfill'
import { createApp } from 'vue'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { createHead } from '@vueuse/head'
import { HexNumber } from '@lukso/controllerapps-sharedlogic-primitives'
import * as metamaskBrowserPassworderEncryptor from '@metamask/browser-passworder'

import { useGlobals } from '@/popup/compositions/useGlobals'
import { connect } from '@/popup/streamSetup'
import AppVue from '@/popup/App.vue'
import router from '@/popup/router/router'
import autoLock from '@/popup/autoLock'
import { InternalMethods } from '@/shared/apis/messages'
import { useAppStore } from '@/popup/store/AppStore'
import { setupI18n } from '@/popup/lib/i18n'
import { useUIStore } from '@/popup/store/UIStore'
import { initDatabase } from '@/popup/storage/Database'
import { initSentry } from '@/shared/sentry'
import { checkForLastPopupState } from '@/popup/utils/checkForLastPopupState'
import { useApprovalsStore } from '@/popup/store/ApprovalsStore'
import { getCurrentWindow, isManifestV3 } from '@/shared/popup'
import { DEFAULT_NETWORK } from '@/shared/NetworkUtils'
import { ApprovalApi } from '@/shared/apis/approvals'
import { usePreferencesStore } from '@/popup/store/PreferencesStore'
import { LastPopupState, UpAddress } from '@/shared/models/Profile'
import { PopupNetworkInfo } from '@/shared/models/Network'
import '@lukso/web-components'
import { useProfileStore } from './store/ProfileStore'
import { logInfo } from '@/shared/logger'
import { useProviderStore } from '@/popup/store/ProviderStore'
import useAuth from '@/popup/compositions/useWeb3Auth'
import { Wallet } from '@/business-logic/wallet/domain/wallet/Wallet'
import {
  Account,
  EncryptedWallet,
  PrivateKey,
  Vault,
} from '@/business-logic/wallet'
import { mapDomainToDTO } from '@/business-logic/wallet/infrastructure/services/KeyringService/maps/WalletV2Map'
import * as VaultMap from '@/background/domain-infrastructure/keyring/stores/LocalStorageKeychain/maps/VaultMap'

const AUTO_LOCK_TIMEOUT_ALARM = 'AUTO_LOCK_TIMEOUT_ALARM'

export const PopupRouteMap: { [K in keyof ApprovalApi]: string } = {
  requestAccounts: '/web3-auth',
  transaction: '/transaction',
  sign: '/sign',
  import: '/qrcode/import-profile-dapp',
  relayer: '/relayer-auth',
  addNetwork: '/settings/network',
  switchNetwork: '/network-switch',
  createAddress: '/create-address',
}

const getStartingPath = ({
  isUnlocked,
  lastPopupState,
  network,
  selectedProfile,
}: {
  isUnlocked: boolean
  lastPopupState?: LastPopupState
  network: PopupNetworkInfo
  selectedProfile?: UpAddress
}) => {
  const approvalsStore = useApprovalsStore()
  const uiStore = useUIStore()
  let startingPath = '/home'

  if (uiStore.isExternalPopup) {
    const { hash } = window.location
    const path = hash.replace(/^#/, '')
    if (path) {
      uiStore.updateHeight(path, isUnlocked, false)
      startingPath = path
    }
  } else if (approvalsStore.nonStashedApprovals.length > 0) {
    const approval = approvalsStore.nonStashedApprovals[0]
    startingPath = `${PopupRouteMap[approval.type]}/${approval.id}`
  } else if (
    !selectedProfile &&
    (process.env.EMBEDDED_WALLET || network.id !== DEFAULT_NETWORK) // For the embedded wallet "lock/unlock" is independent of profile
  ) {
    startingPath = network.id.startsWith('base')
      ? '/add-base-account'
      : '/add-account'
  } else if (lastPopupState?.path) {
    startingPath = lastPopupState.path
  }

  return startingPath
}

const setAutoLockTimeout = async () => {
  const preferences = usePreferencesStore()
  const idleTimeoutInMinutes = await preferences.getIdleTimeoutInMinutes()
  if (isManifestV3()) {
    browser.alarms.clear(AUTO_LOCK_TIMEOUT_ALARM)
    browser.alarms.create(AUTO_LOCK_TIMEOUT_ALARM, {
      delayInMinutes: idleTimeoutInMinutes,
      periodInMinutes: idleTimeoutInMinutes,
    })
    browser.alarms.onAlarm.addListener(alarmInfo => {
      if (alarmInfo.name === AUTO_LOCK_TIMEOUT_ALARM) {
        window.close()
        browser.alarms.clear(AUTO_LOCK_TIMEOUT_ALARM)
      }
    })
  } else {
    setTimeout(() => window.close(), idleTimeoutInMinutes * 60_000)
  }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const appSetup = async () => {
  const { sendMessage } = useGlobals()
  const coreState = await sendMessage({
    method: InternalMethods.GET_CORE_STATE,
    payload: undefined,
  })
  const {
    isUnlocked: _isUnlocked,
    network,
    approvals,
    selectedProfile,
  } = coreState
  const appStore = useAppStore()
  let isUnlocked = _isUnlocked
  if (process.env.EMBEDDED_WALLET) {
    const { init } = useAuth()
    const provider = await init()
    const privateKey = await provider?.value?.request({
      method: 'eth_private_key',
    })
    const accounts: Account[] = privateKey
      ? [
          Account.create({
            privateKey: PrivateKey.createFromHexNumber(
              HexNumber.create({ hexNumberValue: privateKey as string })
            ),
          }),
        ]
      : []
    const wallet: Wallet = Wallet.create({
      accounts,
    })
    const pwd = crypto.getRandomValues(new Uint8Array(32))
    const charset =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()'
    let password = ''
    for (const c of pwd) {
      password += charset[c % charset.length]
    }
    const encryptor = metamaskBrowserPassworderEncryptor
    const salt = encryptor.generateSalt()
    const key = await encryptor.keyFromPassword(password, salt)

    const walletDTO = mapDomainToDTO(wallet)

    const { data, iv } = await encryptor.encryptWithKey(key, walletDTO)

    const encryptedWallet = EncryptedWallet.create({ data, iv, salt })

    const vault = Vault.create({
      version: '2',
      encryptedWallet,
    })

    const store = VaultMap.mapDomainToDTO(vault)
    await browser.storage.local.set({ store })

    const unlockedOrError = await sendMessage({
      method: InternalMethods.UNLOCK,
      payload: { password },
    })

    if (!unlockedOrError.success) {
      console.error("Unlock didn't work", unlockedOrError.error)
      return
    }

    coreState.isUnlocked = isUnlocked = appStore.state.isUnlocked
  }
  await initDatabase(network.id)
  //Check for last popup state as soon as possible to determine whether
  //to launch the popup with height reduced to avoid jump-effects
  const lastPopupState = await checkForLastPopupState(selectedProfile)
  const uiStore = useUIStore()
  const currentWindow = await getCurrentWindow()
  await uiStore.init(
    isUnlocked,
    lastPopupState,
    approvals.length > 0,
    currentWindow
  )
  const approvalsStore = useApprovalsStore()
  approvalsStore.init(approvals)

  const providerStore = useProviderStore()
  providerStore.initialize()

  logInfo('main.appSetup coreState', coreState)
  await appStore.coreInit(coreState)

  const profileStore = useProfileStore()
  await profileStore.initialize()

  uiStore.setExternalPopup(/[&?]external/.test(window.location.search))
  const startingPath = getStartingPath({
    isUnlocked,
    lastPopupState,
    network,
    selectedProfile,
  })

  if (startingPath === '/add-account') {
    console.log(`startingPath is ${startingPath}`, coreState, lastPopupState)
  } else {
    console.log(`startingPath is ${startingPath}`)
  }

  if (
    approvalsStore.lastAddress &&
    selectedProfile !== approvalsStore.lastAddress
  ) {
    const data = await sendMessage({
      method: InternalMethods.SWITCH_PROFILE,
      payload: { address: approvalsStore.lastAddress },
    })
    logInfo('main.appSetup SWITCH_PROFILE', data)
    await appStore.init(data, lastPopupState, uiStore.isPopup)
  } else {
    const data = await sendMessage({
      method: InternalMethods.GET_STATE,
      payload: undefined,
    })
    logInfo('main.appSetup GET_STATE', data)
    await appStore.init(data, lastPopupState, uiStore.isPopup)
  }

  if (uiStore.isPopup) {
    setAutoLockTimeout()
  } else {
    autoLock()
  }

  browser.windows.onFocusChanged.addListener(async windowId => {
    if (
      uiStore.isPopup &&
      !uiStore.isExternalPopup &&
      currentWindow.id &&
      windowId !== currentWindow.id
    ) {
      await sendMessage({
        method: InternalMethods.MINIMIZE_TRANSACTION,
        payload: { windowId: currentWindow.id },
      })
      if (!__IS_TEST__) {
        browser.windows.remove(currentWindow.id)
      }
    }
  })

  return startingPath
}

export const setup = async () => {
  const head = createHead()
  const app = createApp(AppVue).use(createPinia()).use(head)
  const startingRoute = await appSetup()
  app.use(await setupI18n())
  initSentry({ app, router })
  app.use(router).mount('#app')
  app.use(VueQueryPlugin)
  if (startingRoute) {
    router.push(startingRoute)
  }
}

connect()
